import { useEffect, useState } from "react";

import {
  defaultTemplate,
  templateMessagePlaceholders,
  templatePlaceholderDetails,
  TemplateMessageData,
  TemplateResponseData,
  appendDownloadFormLink,
  isValidFormRedirectUrl,
  parseFormRedirectUrl,
} from "@smart/bridge-templates-basic";
import {
  Button,
  ButtonProps,
  Displayer,
  Editor,
  Modal,
  richMessageFeatures,
  Tag,
} from "@smart/itops-sb-design-system-dom";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

import { ConfirmationMode, ConfirmationSection } from "./confirmation-section";
import { ContentCard } from "./content-card";
import { GqlFormCategory } from "../../types";

export type MessageTemplatesPayload = {
  // undefined keeps the form's message
  // null removes it so the firm default applies
  new?: string | null;
  reminder?: string | null;
  confirmation?: string;
  confirmationMode?: ConfirmationMode;
  redirectUrl?: string | null;
};

// an emptied heading or list still serializes its marker
const emptyLine = /^\s*(#{1,6}|-|\d+\.)?\s*$/;

const isEmptyMessage = (message: string) =>
  message.split("\n").every((line) => emptyLine.test(line));

export const toMessagePayload = (
  edited: string | undefined,
  reverted: boolean,
) => {
  if (reverted) return null;
  if (edited === undefined) return undefined;
  return isEmptyMessage(edited) ? null : edited;
};

type MessageTemplatesProps = {
  formCategory?: GqlFormCategory;
  onSave: (messages: MessageTemplatesPayload) => Promise<void>;
  onClose: () => void;
  messageTemplatesModal: boolean;
  currentMessages?: {
    new?: string | null;
    reminder?: string | null;
    confirmation?: string | null;
  };
  currentRedirectUrl?: string | null;
  defaultMessages?: {
    lead?: string | null;
    matter?: string | null;
    reminder?: string | null;
    confirmation?: string | null;
    redirectUrl?: string | null;
  } | null;
  messagePlaceholderData: Partial<TemplateMessageData>;
  responsePlaceholderData: Partial<TemplateResponseData>;
};

export const MessageTemplates = ({
  formCategory,
  onSave,
  onClose,
  messageTemplatesModal,
  currentMessages,
  currentRedirectUrl,
  defaultMessages,
  messagePlaceholderData,
  responsePlaceholderData,
}: MessageTemplatesProps) => {
  const { publishEvent } = useAnalyticsEvent();
  const [editedNewMessage, setEditedNewMessage] = useState<string | undefined>(
    undefined,
  );
  const [editedReminderMessage, setEditedReminderMessage] = useState<
    string | undefined
  >(undefined);
  const [editedConfirmationMessage, setEditedConfirmationMessage] = useState<
    string | undefined
  >(undefined);
  const [editedRedirectUrl, setEditedRedirectUrl] = useState<
    string | undefined
  >(undefined);
  const [reverted, setReverted] = useState({ new: false, reminder: false });
  const [confirmationMode, setConfirmationMode] =
    useState<ConfirmationMode>("message");
  const [editorKeys, setEditorKeys] = useState({
    new: 0,
    reminder: 0,
    confirmation: 0,
  });
  const [loading, setLoading] = useState(false);

  const defaultRedirectUrl = defaultMessages?.redirectUrl;

  useEffect(() => {
    if (!messageTemplatesModal) return;

    setConfirmationMode(currentRedirectUrl ? "redirect" : "message");
    setEditedRedirectUrl(undefined);
  }, [messageTemplatesModal, currentRedirectUrl]);

  const category = formCategory === "matter" ? "matter" : "lead";
  const defaultNewMessage = defaultMessages?.[category];
  const defaultTemplateNewMessage = defaultTemplate[category];

  const newMessageRevertValue = defaultNewMessage || defaultTemplateNewMessage;
  const reminderRevertValue =
    defaultMessages?.reminder || defaultTemplate.reminder;
  const confirmationRevertValue = appendDownloadFormLink(
    defaultMessages?.confirmation || defaultTemplate.response,
  );

  const formNewMessage = reverted.new ? undefined : currentMessages?.new;
  const formReminderMessage = reverted.reminder
    ? undefined
    : currentMessages?.reminder;
  const isNewMessageAtFirmDefault =
    !formNewMessage && editedNewMessage === undefined;
  const isReminderAtFirmDefault =
    !formReminderMessage && editedReminderMessage === undefined;
  const currentConfirmationContent =
    editedConfirmationMessage ??
    appendDownloadFormLink(
      currentMessages?.confirmation ||
        defaultMessages?.confirmation ||
        defaultTemplate.response,
    );
  const currentRedirectUrlValue = editedRedirectUrl ?? currentRedirectUrl ?? "";

  const isRedirectSaveDisabled =
    confirmationMode === "redirect" &&
    !isValidFormRedirectUrl(currentRedirectUrlValue);

  const handleClose = () => {
    setEditedNewMessage(undefined);
    setEditedReminderMessage(undefined);
    setEditedConfirmationMessage(undefined);
    setEditedRedirectUrl(undefined);
    setReverted({ new: false, reminder: false });
    onClose();
  };

  const save = async () => {
    setLoading(true);

    const payload: MessageTemplatesPayload = {
      new: toMessagePayload(editedNewMessage, reverted.new),
      reminder: toMessagePayload(editedReminderMessage, reverted.reminder),
      confirmationMode,
      redirectUrl:
        confirmationMode === "message"
          ? null
          : (parseFormRedirectUrl(currentRedirectUrlValue) ?? null),
    };

    if (confirmationMode === "message") {
      payload.confirmation = editedConfirmationMessage || undefined;
    }

    await onSave(payload);

    if (payload.confirmationMode === "redirect" && payload.redirectUrl) {
      publishEvent?.({
        category: "track",
        name: "IntakeRedirectUrlAdded",
        screen: "FormSettingsMessageTemplates",
        entryPoint: "FormBuilderEditView",
        component: "Confirmation Message",
        jiraKey: "LINK-3632",
        interactionType: "Active",
        properties: {
          scope: "Form",
        },
      }).catch(console.error);
    }

    setLoading(false);
    handleClose();
  };

  const commonButtonProps: ButtonProps = {
    variant: "secondarySubtle",
    size: "small",
    text: "Revert to Firm Default",
  };

  const confirmationFirmDefaultMode: ConfirmationMode = defaultRedirectUrl
    ? "redirect"
    : "message";
  const isConfirmationAtFirmDefault =
    confirmationMode === confirmationFirmDefaultMode &&
    (confirmationFirmDefaultMode === "redirect"
      ? currentRedirectUrlValue === (defaultRedirectUrl ?? "")
      : currentConfirmationContent === confirmationRevertValue);
  const handleConfirmationRevert = () => {
    setConfirmationMode(confirmationFirmDefaultMode);
    if (confirmationFirmDefaultMode === "message") {
      setEditedConfirmationMessage(confirmationRevertValue);
      setEditorKeys((prev) => ({
        ...prev,
        confirmation: prev.confirmation + 1,
      }));
    } else {
      setEditedRedirectUrl(defaultRedirectUrl ?? "");
    }
  };

  const revertMessage = (key: "new" | "reminder") => {
    if (key === "new") setEditedNewMessage(undefined);
    else setEditedReminderMessage(undefined);
    setReverted((prev) => ({ ...prev, [key]: true }));
    setEditorKeys((prev) => ({ ...prev, [key]: prev[key] + 1 }));
  };

  return (
    <Modal
      variant="scrollable"
      loading={loading}
      size="large"
      header={{
        icon: "solidMemo",
        iconBgColor: "bg-cyan-140",
        text: (
          <div className="flex items-center gap-medium">
            <Displayer value="Message Templates" paragraphTag="h4" />
            <Tag text="This form only" />
          </div>
        ),
      }}
      footer={{
        buttons: [
          {
            text: "Cancel",
            variant: "secondarySubtle",
            onClick: handleClose,
          },
          {
            text: "Save",
            variant: "primaryDefault",
            onClick: save,
            disabled: isRedirectSaveDisabled,
          },
        ],
      }}
      open={messageTemplatesModal}
      onClose={handleClose}
    >
      <ContentCard
        title="New Intake Form"
        headerRight={
          <Button
            {...commonButtonProps}
            disabled={isNewMessageAtFirmDefault}
            onClick={() => revertMessage("new")}
          />
        }
      >
        <div className="flex p-[2.4rem] flex-col gap-small">
          <div className="flex max-h-[32.15rem] flex-col">
            <Editor
              key={editorKeys.new}
              id="new-message"
              dataTestId="new-message-editor"
              initialTextValue={
                editedNewMessage || formNewMessage || newMessageRevertValue
              }
              onValueChange={(value) => {
                setEditedNewMessage(value);
                setReverted((prev) => ({ ...prev, new: false }));
              }}
              placeholders={{
                keys: templateMessagePlaceholders,
                details: templatePlaceholderDetails,
                data: messagePlaceholderData,
              }}
              featureOptions={richMessageFeatures}
              mandatory
            />
          </div>
        </div>
      </ContentCard>
      <ContentCard
        title="Reminder"
        headerRight={
          <Button
            {...commonButtonProps}
            disabled={isReminderAtFirmDefault}
            onClick={() => revertMessage("reminder")}
          />
        }
      >
        <div className="flex p-[2.4rem] flex-col gap-small">
          <div className="flex max-h-[32.15rem] flex-col">
            <Editor
              key={editorKeys.reminder}
              id="reminder-message"
              dataTestId="reminder-message-editor"
              initialTextValue={
                editedReminderMessage ||
                formReminderMessage ||
                reminderRevertValue
              }
              onValueChange={(value) => {
                setEditedReminderMessage(value);
                setReverted((prev) => ({ ...prev, reminder: false }));
              }}
              placeholders={{
                keys: templateMessagePlaceholders,
                details: templatePlaceholderDetails,
                data: messagePlaceholderData,
              }}
              featureOptions={richMessageFeatures}
              mandatory
            />
          </div>
        </div>
      </ContentCard>
      <ContentCard
        title="Confirmation"
        headerRight={
          <Button
            {...commonButtonProps}
            disabled={isConfirmationAtFirmDefault}
            onClick={handleConfirmationRevert}
          />
        }
      >
        <ConfirmationSection
          mode={confirmationMode}
          onModeChange={setConfirmationMode}
          currentConfirmationContent={currentConfirmationContent}
          editedConfirmationMessage={editedConfirmationMessage}
          onConfirmationChange={setEditedConfirmationMessage}
          editorKey={editorKeys.confirmation}
          responsePlaceholderData={responsePlaceholderData}
          redirectUrl={currentRedirectUrlValue}
          onRedirectUrlChange={setEditedRedirectUrl}
        />
      </ContentCard>
    </Modal>
  );
};
